/*
  Flexify Theme — theme.js
  Vanilla ES module. No external libraries, no jQuery.
  Individual components (cart drawer, predictive search, mobile menu, etc.)
  register themselves as Web Components or in their own dedicated JS file
  (cart.js, product.js, collection.js, menu.js); this file only holds
  cross-cutting helpers shared by more than one component.
*/

export function debounce(fn, wait) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), wait);
  };
}

export function trapFocus(container, onEscape) {
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function handleKeydown(event) {
    if (event.key === 'Escape' && onEscape) {
      onEscape();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = Array.from(container.querySelectorAll(focusableSelector));
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  container.addEventListener('keydown', handleKeydown);
  return () => container.removeEventListener('keydown', handleKeydown);
}

/*
  Scroll-reveal: sections opt in with [data-scroll-reveal]. Uses opacity/
  transform only (no layout-affecting properties), so it never contributes
  to CLS even for above-the-fold sections. Skipped entirely when the visitor
  has requested reduced motion, and skipped entirely inside the theme editor
  (Shopify.designMode) since the editor re-injects section HTML dynamically
  whenever a setting changes, which would otherwise leave content stuck
  invisible with no observer watching the new element.
*/
function revealElement(el) {
  el.classList.add('is-visible');
}

function initScrollReveal(root = document) {
  const revealEls = root.querySelectorAll('[data-scroll-reveal]:not(.is-visible)');
  if (!revealEls.length) return;

  const isEditor = typeof window.Shopify !== 'undefined' && window.Shopify.designMode;
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (isEditor || prefersReduced) {
    revealEls.forEach(revealElement);
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          revealElement(entry.target);
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.1, rootMargin: '0px 0px -5% 0px' }
  );

  revealEls.forEach((el) => observer.observe(el));
}

document.addEventListener('DOMContentLoaded', () => initScrollReveal());

// Re-scan whenever the theme editor swaps in fresh section HTML
document.addEventListener('shopify:section:load', (event) => initScrollReveal(event.target));

/**
 * --scrollbar-w — the real width of the vertical scrollbar.
 *
 * Full-bleed rules built on 100vw overshoot, because 100vw counts the
 * scrollbar while a parent's 50% does not. On a 1367px window with a 15px bar
 * that is 7.5px past each edge, which makes the page scroll sideways. CSS has
 * no way to measure it, so it is measured once here and exposed as a variable
 * the stylesheets can subtract.
 *
 * Zero on overlay-scrollbar platforms (most phones, macOS by default), where
 * there is nothing to correct for.
 */
(function setScrollbarWidth() {
  let last = null;

  function measure() {
    const width = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    // Only write when the value actually changes. The variable feeds the
    // header band's width, so writing it changes layout, which the observer
    // below sees as a resize — without this guard that is a feedback loop
    // and the browser reports "ResizeObserver loop completed with undelivered
    // notifications". Returning early lets it settle after one pass.
    if (width === last) return;
    last = width;
    document.documentElement.style.setProperty('--scrollbar-w', `${width}px`);
  }

  measure();

  // The bar appears and disappears as content grows, and changes on zoom.
  window.addEventListener('resize', measure, { passive: true });
  if ('ResizeObserver' in window) {
    new ResizeObserver(measure).observe(document.documentElement);
  }
})();

/* ---------------------------------------------------------------------------
   Moved out of snippets/product-card.liquid. Shopify discards
   {% javascript %} inside a snippet, so none of this ever ran.

   It lives in theme.js rather than collection.js because product cards render
   on the homepage, product pages and cart recommendations too, and
   collection.js only loads on the collection and search templates.
--------------------------------------------------------------------------- */

  /* Review widget re-initialisation.

     Review apps scan the DOM once on page load. The collection grid is
     replaced on every filter or sort change and appended to by Load More, so
     badges render on first paint and then disappear — or never appear on
     products loaded later.

     Each call is feature-detected, so this is a no-op on a store with no
     review app and does not need to know which one is installed. */
  (() => {
    function refreshReviewWidgets() {
      try {
        if (window.jdgm && typeof window.jdgm.customizeBadges === 'function') {
          window.jdgm.customizeBadges();
        }
        if (window.Loox && typeof window.Loox.reload === 'function') {
          window.Loox.reload();
        }
        if (window.yotpo && typeof window.yotpo.refreshWidgets === 'function') {
          window.yotpo.refreshWidgets();
        }
        if (window.StampedFn && typeof window.StampedFn.loadBadges === 'function') {
          window.StampedFn.loadBadges();
        }
        if (window.okeWidgetApi && typeof window.okeWidgetApi.initWidget === 'function') {
          window.okeWidgetApi.initWidget();
        }
      } catch (error) {
        /* A review app throwing is not a reason to break the grid. */
      }
    }

    document.addEventListener('neroli:products:rendered', refreshReviewWidgets);
  })();

  /* Quick add drawer.

     Lives in the card snippet so it ships wherever cards do — Shopify folds a
     snippet's javascript block into the theme bundle once, however many times
     the snippet renders.

     Native <dialog> is used deliberately: showModal() brings a focus trap,
     Escape handling, inertness of the page behind and a backdrop for free,
     none of which is worth reimplementing. */
  (() => {
    let dialog = null;
    let returnFocus = null;

    function buildDialog() {
      if (dialog) return dialog;

      dialog = document.createElement('dialog');
      dialog.className = 'quick-add-dialog';
      dialog.setAttribute('aria-label', window.NeroliStrings?.quickAdd || 'Quick add');
      // A titled header rather than a floating close button: the dialog needs
      // a visible name, and the header is also what the scrollable body and
      // sticky footer are measured against.
      dialog.innerHTML =
        '<div class="quick-add-dialog__header">' +
        '<h2 class="quick-add-dialog__heading">' +
        (window.NeroliStrings?.chooseOptions || 'Choose options') +
        '</h2>' +
        '<button type="button" class="quick-add-dialog__close" data-quick-add-close>' +
        '<span class="visually-hidden">' +
        (window.NeroliStrings?.close || 'Close') +
        '</span>' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" ' +
        'stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" ' +
        'stroke-linecap="round"/></svg></button>' +
        '</div>' +
        '<div class="quick-add-dialog__body" data-quick-add-body></div>';

      document.body.appendChild(dialog);

      // Clicks land on the dialog element itself only when they hit the backdrop.
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });

      /* Accelerated checkout submits the form and navigates. That round trip
         is Shopify's, not ours, and until the new page paints the drawer just
         sits there looking idle while the browser's tab spinner turns — which
         reads as a dead button. This marks the drawer as busy so the press is
         visibly acknowledged. */
      dialog.addEventListener('submit', (event) => {
        if (event.submitter && event.submitter.hasAttribute('data-add-to-cart')) return;
        dialog.classList.add('is-navigating');
      });

      dialog.addEventListener('close', () => {
        dialog.classList.remove('is-navigating');
        dialog.querySelector('[data-quick-add-body]').innerHTML = '';
        if (returnFocus && document.contains(returnFocus)) returnFocus.focus();
        returnFocus = null;
      });

      return dialog;
    }

    /* Responses are kept so reopening the same product is instant, and are
       started on hover so the request is usually finished before the click
       lands. The dialog was showing "Loading…" for the whole round trip
       because the fetch only began once the customer had already committed. */
    const cache = new Map();

    function loadSection(url) {
      if (cache.has(url)) return cache.get(url);

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);

      const request = fetch(
        url + (url.includes('?') ? '&' : '?') + 'section_id=quick-add',
        { signal: controller.signal }
      )
        .then((response) => {
          clearTimeout(timeout);
          if (!response.ok) throw new Error(String(response.status));
          return response.text();
        })
        .then((html) => {
          if (!html.trim()) throw new Error('empty');
          return html;
        })
        .catch((error) => {
          // A cached rejection would break this product for the rest of the
          // session, so the failure is dropped and the next attempt retries.
          cache.delete(url);
          throw error;
        });

      cache.set(url, request);
      return request;
    }

    /* Warm the cache on intent rather than on click. pointerenter covers
       mouse and pen; touchstart fires a moment before the click on touch,
       which is still enough to hide most of the latency. */
    let warmTimer = null;

    function warm(event) {
      const trigger = event.target.closest?.('[data-quick-add-url]');
      if (!trigger) return;
      const url = trigger.dataset.quickAddUrl;
      if (cache.has(url)) return;

      // A short delay so sweeping the pointer across a grid does not fire a
      // request for every card it passes over.
      clearTimeout(warmTimer);
      warmTimer = setTimeout(() => loadSection(url).catch(() => {}), 120);
    }

    document.addEventListener('pointerenter', warm, true);
    document.addEventListener('focusin', warm);
    document.addEventListener('touchstart', warm, { passive: true });

    document.addEventListener('click', async (event) => {
      if (event.target.closest('[data-quick-add-close]')) {
        dialog?.close();
        return;
      }

      const trigger = event.target.closest('[data-quick-add-url]');
      if (!trigger) return;

      // Let modified clicks open the product page in a new tab as normal.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

      event.preventDefault();
      returnFocus = trigger;

      const url = trigger.dataset.quickAddUrl;
      const modal = buildDialog();
      const body = modal.querySelector('[data-quick-add-body]');

      /* A skeleton in the shape of the real content, not a line of text. It
         holds the drawer's layout so nothing jumps when the markup lands,
         and it reads as progress rather than as a stall. */
      body.innerHTML =
        '<div class="quick-add-skeleton" role="status">' +
        '<span class="visually-hidden">' +
        (window.NeroliStrings?.loading || 'Loading…') +
        '</span>' +
        '<div class="quick-add-skeleton__intro">' +
        '<div class="quick-add-skeleton__media"></div>' +
        '<div class="quick-add-skeleton__lines">' +
        '<span class="quick-add-skeleton__line quick-add-skeleton__line--short"></span>' +
        '<span class="quick-add-skeleton__line"></span>' +
        '<span class="quick-add-skeleton__line quick-add-skeleton__line--mid"></span>' +
        '</div></div>' +
        '<div class="quick-add-skeleton__options">' +
        '<span class="quick-add-skeleton__chip"></span>' +
        '<span class="quick-add-skeleton__chip"></span>' +
        '<span class="quick-add-skeleton__chip"></span>' +
        '</div></div>';

      modal.showModal();

      try {
        body.innerHTML = await loadSection(url);
      } catch (error) {
        // The href is a real product URL, so failure degrades to opening the
        // product page rather than leaving the customer stuck.
        modal.close();
        window.location.href = url;
      }
    });

    // cart.js opens its own drawer after a successful add; two overlapping
    // dialogs would fight over focus.
    document.addEventListener('cart:updated', () => dialog?.close());
  })();